import React, { useState } from 'react';
import { 
  AlertTriangle, 
  Clock, 
  X, 
  ExternalLink,
  Users,
  MessageSquare,
  Target,
  Calendar,
  BookOpen
} from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

/**
 * CriticalAlerts - Display critical notifications as full alerts in top area
 * This component should be placed near the top of the main layout
 */
const CriticalAlerts = ({ className = '' }) => {
  const { 
    criticalAlerts, 
    dismissCriticalAlert, 
    dismissAllCriticalAlerts,
    markAsRead 
  } = useNotifications();
  const navigate = useNavigate();
  const [collapsedAlerts, setCollapsedAlerts] = useState(new Set());

  // Entity type icons
  const entityIcons = {
    'Customer': Users,
    'Lead': MessageSquare,
    'Opportunity': Target,
    'Activity': Calendar,
    'Knowledge': BookOpen
  };

  // Don't render if no critical alerts
  if (!criticalAlerts || criticalAlerts.length === 0) {
    return null;
  }

  const handleDismiss = async (alertId) => {
    try {
      // Mark as read in backend if it's a notification
      await markAsRead(alertId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
    
    // Remove from critical alerts display
    dismissCriticalAlert(alertId);
  };

  const handleNavigateToEntity = (entityType, entityId) => {
    // Navigate to the relevant entity page
    const routes = {
      'Customer': `/customers/${entityId}`,
      'Lead': `/leads/${entityId}`,
      'Opportunity': `/opportunities/${entityId}`,
      'Activity': `/activities/${entityId}`,
      'Knowledge': `/knowledge/${entityId}`
    };

    const route = routes[entityType];
    if (route) {
      navigate(route);
    }
  };

  const toggleCollapse = (alertId) => {
    setCollapsedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Header with dismiss all option */}
      {criticalAlerts.length > 1 && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <span className="text-sm font-medium text-gray-700">
              Critical Alerts ({criticalAlerts.length})
            </span>
          </div>
          <button
            onClick={dismissAllCriticalAlerts}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Dismiss All
          </button>
        </div>
      )}

      {/* Critical Alerts */}
      {criticalAlerts.map((alert) => {
        const EntityIcon = entityIcons[alert.entityType] || AlertTriangle;
        const isCollapsed = collapsedAlerts.has(alert.id);

        return (
          <div
            key={alert.id}
            className="bg-red-50 border-l-4 border-red-400 rounded-lg shadow-sm overflow-hidden"
          >
            {/* Alert Header */}
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-red-100 rounded-full">
                      <EntityIcon className="h-5 w-5 text-red-600" />
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-sm font-medium text-red-800">
                        {alert.title}
                      </h3>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        {alert.entityType}
                      </span>
                    </div>
                    
                    {!isCollapsed && (
                      <div className="space-y-2">
                        <p className="text-sm text-red-700">
                          {alert.message}
                        </p>
                        
                        <div className="flex items-center space-x-4 text-xs text-red-600">
                          <div className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>{formatTimeAgo(alert.createdAt)}</span>
                          </div>
                          
                          {alert.entityId && (
                            <button
                              onClick={() => handleNavigateToEntity(alert.entityType, alert.entityId)}
                              className="flex items-center space-x-1 hover:text-red-800 underline"
                            >
                              <ExternalLink className="h-3 w-3" />
                              <span>View {alert.entityType}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 ml-4">
                  {/* Collapse/Expand */}
                  <button
                    onClick={() => toggleCollapse(alert.id)}
                    className="p-1 text-red-400 hover:text-red-600 rounded-full hover:bg-red-100"
                    title={isCollapsed ? 'Expand' : 'Collapse'}
                  >
                    <div className={`transform transition-transform ${isCollapsed ? 'rotate-180' : ''}`}>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {/* Dismiss */}
                  <button
                    onClick={() => handleDismiss(alert.id)}
                    className="p-1 text-red-400 hover:text-red-600 rounded-full hover:bg-red-100"
                    title="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* Collapsed State Summary */}
              {isCollapsed && (
                <div className="mt-2 text-xs text-red-600">
                  <div className="flex items-center justify-between">
                    <span className="truncate">
                      {alert.message.substring(0, 60)}...
                    </span>
                    <span className="ml-2 flex-shrink-0">
                      {formatTimeAgo(alert.createdAt)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * CriticalAlertBanner - Minimal banner version for small spaces
 */
export const CriticalAlertBanner = ({ maxAlerts = 3, className = '' }) => {
  const { criticalAlerts, dismissCriticalAlert } = useNotifications();

  if (!criticalAlerts || criticalAlerts.length === 0) {
    return null;
  }

  const displayAlerts = criticalAlerts.slice(0, maxAlerts);
  const remainingCount = criticalAlerts.length - maxAlerts;

  return (
    <div className={`bg-red-100 border border-red-300 rounded-lg p-3 ${className}`}>
      <div className="flex items-center space-x-2">
        <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-red-800">
            <span className="font-medium">{displayAlerts.length} Critical Alert{displayAlerts.length !== 1 ? 's' : ''}</span>
            {remainingCount > 0 && <span className="ml-1">+{remainingCount} more</span>}
          </div>
          <div className="text-xs text-red-600 mt-1">
            {displayAlerts[0]?.title} - {displayAlerts[0]?.message.substring(0, 50)}...
          </div>
        </div>
        <button
          onClick={() => dismissCriticalAlert(displayAlerts[0]?.id)}
          className="p-1 text-red-400 hover:text-red-600 rounded-full hover:bg-red-200"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};

export default CriticalAlerts;